/**
 * SSOLanding — /sso?token=<jwt>&to=<module>
 * The WMS hands the browser here with a signed one-shot token; we exchange it
 * for a NicheSocial session so the agent never sees a second login screen.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Loader2, ShieldCheck, ShieldAlert } from "lucide-react";
import { useAuth } from "./AuthContext";
import "./AuthPage.css";

const API = process.env.REACT_APP_BACKEND_URL;

export default function SSOLanding({ onSuccess, onFallbackLogin }) {
  const { loginWithToken } = useAuth();
  const [error, setError] = useState("");
  const [status, setStatus] = useState("Checking your WMS sign-in…");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    const to = params.get("to");
    if (!token) {
      setError("No sign-in token was included in this link.");
      return;
    }
    (async () => {
      try {
        setStatus("Signing you in…");
        const r = await axios.post(`${API}/api/auth/sso/wms`, { token });
        loginWithToken(r.data.access_token, r.data.user);
        setStatus(`Welcome back, ${r.data.user?.name?.split(" ")[0] || "there"}`);
        onSuccess && onSuccess(r.data.module || to || null, r.data.user);
      } catch (e) {
        const d = e?.response?.data?.detail;
        setError(typeof d === "string" ? d : "We couldn't verify this sign-in link.");
      }
    })();
  }, [loginWithToken, onSuccess]);

  return (
    <div className="auth-page" data-testid="sso-landing">
      <div className="auth-container" style={{ textAlign: "center" }}>
        <img
          src="https://customer-assets.emergentagent.com/job_scriptbuilder/artifacts/6hlprpgp_Green_Logo.png"
          alt="NicheCom"
          className="auth-logo-img"
        />
        {!error ? (
          <>
            <div style={{ display: "flex", justifyContent: "center", margin: "18px 0 10px" }}>
              <div style={{ width: 62, height: 62, borderRadius: "50%", background: "rgba(150,193,31,0.14)", display: "flex", alignItems: "center", justifyContent: "center", color: "#7aa818" }}>
                <ShieldCheck size={30} />
              </div>
            </div>
            <h1 className="auth-title" data-testid="sso-status">{status}</h1>
            <p className="auth-subtitle">Coming through from WMS — no password needed.</p>
            <div style={{ display: "flex", justifyContent: "center", marginTop: 18, color: "#96C11F" }}>
              <Loader2 className="animate-spin" size={22} />
            </div>
          </>
        ) : (
          <>
            <div style={{ display: "flex", justifyContent: "center", margin: "18px 0 10px" }}>
              <div style={{ width: 62, height: 62, borderRadius: "50%", background: "rgba(239,68,68,0.12)", display: "flex", alignItems: "center", justifyContent: "center", color: "#dc2626" }}>
                <ShieldAlert size={30} />
              </div>
            </div>
            <h1 className="auth-title">Sign-in link didn't work</h1>
            <p className="auth-subtitle" data-testid="sso-error" style={{ color: "#b91c1c" }}>{error}</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 20 }}>
              <button
                type="button"
                className="auth-submit-btn"
                style={{ width: "100%", padding: "12px 16px", borderRadius: 10, border: "none", background: "#96C11F", color: "#10240f", fontWeight: 700, cursor: "pointer" }}
                onClick={onFallbackLogin}
                data-testid="sso-fallback-login"
              >
                Sign in with email instead
              </button>
              <span style={{ fontSize: 12.5, color: "#6a707d" }}>Or go back to WMS and open the tool again.</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
